import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/ui/button/button';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Sparkles, Rocket } from 'lucide-react';
import { activateUpdateAndReload } from 'src/utils/cache-utils';
import ENV from 'src/env.json';

/**
 * Periodic sync check interval — checks for new SW version every hour.
 */
const SYNC_PERIOD = 60 * 60 * 1000;

/**
 * Grace period before the gentle banner becomes a blocking modal.
 * Gives users time to finish current work before forcing the update.
 */
const FORCE_UPDATE_DELAY = 2 * 60 * 1000; // 2 minutes

const registerPeriodicSync = (period: number, swUrl: string, r: ServiceWorkerRegistration) => {
  if (period <= 0) return;

  setInterval(async () => {
    if ('onLine' in navigator && !navigator.onLine) return;

    try {
      const resp = await fetch(swUrl, {
        cache: 'no-store',
        headers: {
          cache: 'no-store',
          'cache-control': 'no-cache',
        },
      });

      if (resp?.status === 200) await r.update();
    } catch (e) {
      console.error('cannot ping/update sw.js', e);
    }
  }, period);
};

// QA testing: add ?force-update to URL (only on localhost and sandbox)
const forceTest =
  (ENV.NODE_ENV === 'dev' || ENV.NODE_ENV === 'sandbox') &&
  new URLSearchParams(window.location.search).has('force-update');

const AppUpdateDialog = () => {
  const { t } = useTranslation();
  const [updateAvailable, setUpdateAvailable] = useState(forceTest);
  const [forceBlocking, setForceBlocking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useRegisterSW({
    onNeedRefresh() {
      setUpdateAvailable(true);
    },
    onRegisteredSW(swUrl, r) {
      if (SYNC_PERIOD <= 0) return;
      if (r?.active?.state === 'activated') {
        registerPeriodicSync(SYNC_PERIOD, swUrl, r);
      } else if (r?.installing) {
        r.installing.addEventListener('statechange', (e) => {
          const sw = e.target as ServiceWorker;
          if (sw.state === 'activated') registerPeriodicSync(SYNC_PERIOD, swUrl, r);
        });
      }
    },
  });

  // Start the countdown to blocking modal when banner appears
  useEffect(() => {
    if (!updateAvailable) return;

    timerRef.current = setTimeout(() => {
      setForceBlocking(true);
    }, FORCE_UPDATE_DELAY);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [updateAvailable]);

  const handleUpdate = useCallback(async () => {
    setIsUpdating(true);
    await activateUpdateAndReload();
  }, []);

  // Phase 2: Blocking full-screen modal (after grace period)
  if (updateAvailable && forceBlocking) {
    return (
      <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-neutral-300/80 tw-backdrop-blur-sm">
        <div
          className="tw-relative tw-mx-4 tw-w-full tw-max-w-[420px] tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl"
          style={{ animation: 'fadeInScale 0.3s ease-out' }}
        >
          {/* Decorative gradient header */}
          <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-bg-gradient-to-br tw-from-purple-900 tw-via-purple-500 tw-to-purple-400 tw-px-8 tw-pb-2 tw-pt-10">
            {/* Floating decorative elements */}
            <Sparkles
              className="tw-absolute tw-left-6 tw-top-4 tw-h-4 tw-w-4 tw-animate-bounce tw-text-white/30"
              style={{ animationDelay: '0.2s' }}
            />
            <Sparkles
              className="tw-absolute tw-right-8 tw-top-6 tw-h-3 tw-w-3 tw-animate-bounce tw-text-brand-bubble/40"
              style={{ animationDelay: '0.5s' }}
            />
            <Sparkles
              className="tw-absolute tw-bottom-6 tw-left-12 tw-h-3.5 tw-w-3.5 tw-animate-bounce tw-text-white/20"
              style={{ animationDelay: '0.8s' }}
            />

            {/* Icon */}
            <div className="tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-white/15 tw-backdrop-blur-sm">
              <Rocket
                className={`tw-h-8 tw-w-8 tw-text-white ${isUpdating ? 'tw-animate-bounce' : ''}`}
              />
            </div>

            {/* Title on purple bg */}
            <h2 className="tw-mb-6 tw-text-xl tw-font-bold tw-text-white">
              {t('Update Required')}
            </h2>
          </div>

          {/* Content area */}
          <div className="tw-px-8 tw-pb-8 tw-pt-6">
            <p className="tw-mb-6 tw-text-center tw-text-sm tw-leading-relaxed tw-text-neutral-600">
              {t(
                'A new version of BeCreatives has been deployed. Please update now to continue using the app.',
              )}
            </p>

            <Button
              className="tw-w-full tw-rounded-xl tw-bg-purple-900 tw-py-3 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-purple-600"
              size="lg"
              onClick={handleUpdate}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <RefreshCw className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" />
                  {t('Updating...')}
                </>
              ) : (
                <>
                  <RefreshCw className="tw-mr-2 tw-h-4 tw-w-4" />
                  {t('Update & Reload')}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Phase 1: Gentle bottom banner (first 2 minutes)
  if (updateAvailable) {
    return (
      <div
        className="tw-fixed tw-inset-x-0 tw-bottom-6 tw-z-[9999] tw-flex tw-justify-center tw-px-4"
        style={{ animation: 'slideUp 0.4s ease-out' }}
      >
        <div className="tw-flex tw-items-center tw-gap-4 tw-rounded-2xl tw-border tw-border-neutral-200 tw-bg-white tw-px-5 tw-py-3 tw-shadow-lg">
          <div className="tw-flex tw-items-center tw-gap-3">
            {/* Purple icon badge */}
            <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-purple-500">
              <Sparkles
                className={`tw-h-5 tw-w-5 tw-text-white ${isUpdating ? 'tw-animate-spin' : ''}`}
              />
            </div>

            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-neutral-900">
                {t('New update available')}
              </p>
              <p className="tw-text-xs tw-text-neutral-500">
                {t('Refresh to get the latest features')}
              </p>
            </div>
          </div>

          <Button
            size="sm"
            className="tw-ml-1 tw-shrink-0 tw-rounded-xl tw-bg-purple-900 tw-px-5 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-purple-600"
            onClick={handleUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <RefreshCw className="tw-mr-1.5 tw-h-3.5 tw-w-3.5 tw-animate-spin" />
                {t('Updating...')}
              </>
            ) : (
              t('Update Now')
            )}
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default AppUpdateDialog;
